import {
  defineDoctype,
  type DoctypeCatalogMetadata,
  type DoctypeChildCascadePolicy,
  type DoctypeDefinition,
} from "@kubdee/sdk";
import { aiAgentChildren } from "./schemas/ai-agent-children";
import { aiAgentKnowledge } from "./schemas/ai-agent-knowledge";
import { aiAgentTools } from "./schemas/ai-agent-tools";
import { aiAgents } from "./schemas/ai-agents";
import { aiKnowledgeCategories } from "./schemas/ai-knowledge-categories";
import { aiKnowledgeChunks } from "./schemas/ai-knowledge-chunks";
import { aiKnowledgeDocs } from "./schemas/ai-knowledge-docs";
import { aiTenantProviders } from "./schemas/tenant-providers";

// ============================================================================
// AI module — registered doctypes.
//
// Phase 0E of #644 keeps this metadata-only. The AI routes still own all
// create/update/delete behavior; generic writes stay denied by policy.
// ============================================================================

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const INTERNAL_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: false,
    isExportable: false,
    isReportable: false,
  },
};

const CHILD_TABLE_CATALOG = (
  parentDoctype: string,
  parentField: string,
  parentKeyField: string,
  sortField?: string,
  cascadePolicy: DoctypeChildCascadePolicy = "restrict",
  policy: DoctypeCatalogMetadata["policy"] = {
    isExportable: true,
    isReportable: true,
  },
): DoctypeCatalogMetadata => ({
  childTable: {
    parentDoctype,
    parentField,
    parentKeyField,
    sortField: sortField ?? null,
    cascadePolicy,
  },
  policy,
});

const JUNCTION_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isExportable: true,
    isReportable: true,
  },
};

export const agentDoctype = defineDoctype({
  id: "agent",
  moduleId: "ai",
  table: aiAgents,
  label: "AI Agent",
  icon: "Bot",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  displayField: "name",
  searchFields: ["name", "description", "agent_type", "ai_model"],
  columnLabels: {
    name: "ชื่อ Agent",
    description: "รายละเอียด",
    system_prompt: "Instruction",
    agent_type: "ประเภท Agent",
    ai_provider: "ผู้ให้บริการ AI",
    ai_model: "โมเดล",
    api_key_source: "แหล่ง API Key",
    temperature: "Temperature",
    max_steps: "จำนวนขั้นสูงสุด",
    is_default: "ค่าเริ่มต้น",
    is_active: "เปิดใช้งาน",
  },
});

export const agentChildDoctype = defineDoctype({
  id: "agent_child",
  moduleId: "ai",
  table: aiAgentChildren,
  label: "Sub-agent",
  icon: "Network",
  features: [],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("agent", "children", "parent_agent_id", "sort_order"),
  displayField: "routing_hint",
  searchFields: ["routing_hint"],
  columnLabels: {
    parent_agent_id: "Agent หลัก",
    child_agent_id: "Sub-agent",
    routing_hint: "เงื่อนไขการส่งต่อ",
    sort_order: "ลำดับ",
    is_enabled: "เปิดใช้งาน",
  },
});

export const agentToolDoctype = defineDoctype({
  id: "agent_tool",
  moduleId: "ai",
  table: aiAgentTools,
  label: "เครื่องมือของ Agent",
  icon: "Wrench",
  features: [],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("agent", "tools", "agent_id"),
  displayField: "tool_name",
  searchFields: ["tool_name"],
  columnLabels: {
    agent_id: "Agent",
    tool_name: "เครื่องมือ",
    is_enabled: "เปิดใช้งาน",
  },
});

export const knowledgeDocDoctype = defineDoctype({
  id: "knowledge_doc",
  moduleId: "ai",
  table: aiKnowledgeDocs,
  label: "เอกสารความรู้",
  icon: "BookOpen",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  displayField: "title",
  searchFields: ["title", "content", "category", "tags"],
  columnLabels: {
    title: "ชื่อเอกสาร",
    content: "เนื้อหา",
    category: "หมวดหมู่เดิม",
    tags: "แท็ก",
    search_mode: "โหมดค้นหา",
    is_active: "เปิดใช้งาน",
    is_vectorized: "ทำ Vector แล้ว",
    chunk_count: "จำนวน Chunk",
  },
});

export const knowledgeChunkDoctype = defineDoctype({
  id: "knowledge_chunk",
  moduleId: "ai",
  table: aiKnowledgeChunks,
  label: "Chunk ความรู้",
  icon: "Database",
  features: [],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG(
    "knowledge_doc",
    "chunks",
    "doc_id",
    "chunk_index",
    "restrict",
    {
      isReadOnly: true,
      isExportable: true,
      isReportable: false,
      isSearchable: false,
    },
  ),
  displayField: "content",
  searchFields: ["content"],
  columnLabels: {
    doc_id: "เอกสารความรู้",
    chunk_index: "ลำดับ Chunk",
    content: "เนื้อหา Chunk",
    vector_id: "Vector ID",
  },
});

export const agentKnowledgeDoctype = defineDoctype({
  id: "agent_knowledge",
  moduleId: "ai",
  table: aiAgentKnowledge,
  label: "เอกสารของ Agent",
  icon: "Link",
  features: [],
  tier: 2,
  kind: "junction",
  catalog: JUNCTION_CATALOG,
  displayField: "id",
  columnLabels: {
    agent_id: "Agent",
    doc_id: "เอกสารความรู้",
    is_enabled: "เปิดใช้งาน",
  },
});

export const knowledgeCategoryDoctype = defineDoctype({
  id: "knowledge_category",
  moduleId: "ai",
  table: aiKnowledgeCategories,
  label: "หมวดหมู่ความรู้",
  icon: "FolderOpen",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  displayField: "name",
  searchFields: ["name", "color", "icon"],
  columnLabels: {
    name: "ชื่อหมวดหมู่",
    color: "สี",
    icon: "ไอคอน",
    sort_order: "ลำดับ",
    is_active: "เปิดใช้งาน",
  },
});

export const tenantProviderDoctype = defineDoctype({
  id: "tenant_provider",
  moduleId: "ai",
  table: aiTenantProviders,
  label: "ตั้งค่า AI Provider",
  icon: "KeyRound",
  features: ["permissions"],
  tier: 2,
  kind: "system",
  catalog: INTERNAL_CATALOG,
  displayField: "provider",
  searchFields: ["provider", "last_test_status"],
  columnLabels: {
    provider: "ผู้ให้บริการ",
    api_key: "API Key",
    account_id: "Account ID",
    is_enabled: "เปิดใช้งาน",
    last_test_status: "สถานะทดสอบล่าสุด",
    last_tested_at: "เวลาทดสอบล่าสุด",
  },
});

export const aiDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  agentDoctype,
  agentChildDoctype,
  agentToolDoctype,
  knowledgeDocDoctype,
  knowledgeChunkDoctype,
  agentKnowledgeDoctype,
  knowledgeCategoryDoctype,
  tenantProviderDoctype,
]);
